/**
 * 学员端 - U币中心页面
 * 
 * 功能:
 * 1. 展示四类U币余额和总U币
 * 2. 快捷入口: U币明细、我的纠错
 * 3. U币获取方式说明
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, getSupabaseUrl } from '../supabase/client';

interface PointAccount {
  id: string;
  user_id: string;
  role: string;
  purchase_rebate_points: number;
  error_correction_points: number;
  activity_reward_points: number;
  teacher_gift_points: number;
  total_points: number;
}

const PointCenter: React.FC = () => {
  const navigate = useNavigate();
  const [userId, setUserId] = useState<string | null>(null);
  const [account, setAccount] = useState<PointAccount | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getCurrentUser();
  }, []);

  const getCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      setUserId(user.id);
      fetchAccount(user.id);
    } else {
      setLoading(false);
    }
  };

  const fetchAccount = async (uid: string) => {
    try {
      setLoading(true);

      // 先尝试获取账户,如果不存在则创建
      const { data: existingAccount, error: fetchError } = await supabase
        .from('user_point_accounts')
        .select('*')
        .eq('user_id', uid)
        .eq('role', 'student')
        .maybeSingle();

      if (existingAccount) {
        setAccount(existingAccount);
      } else {
        // 创建新账户（maybeSingle返回null说明不存在，或fetchError为PGRST116说明无匹配行）
        const session = await supabase.auth.getSession();
        const response = await fetch(`${getSupabaseUrl()}/functions/v1/point-service`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.data.session?.access_token}`,
          },
          body: JSON.stringify({
            action: 'create_account',
            user_id: uid,
            role: 'student',
          }),
        });

        const result = await response.json();
        if (result.success) {
          setAccount(result.account);
        } else {
          console.error('Failed to create account:', result.error);
        }
      }
    } catch (error) {
      console.error('Failed to fetch account:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">加载中...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-xl font-bold text-gray-800">U币中心</h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* 总U币卡片 */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl p-6 text-white shadow-lg">
          <div className="text-center">
            <div className="text-sm opacity-90 mb-2">我的总U币</div>
            <div className="text-5xl font-bold mb-2">{account?.total_points || 0}</div>
            <div className="text-xs opacity-75">U币可抵扣订单金额,100U币=1元</div>
          </div>
        </div>

        {/* 快捷入口 */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">快捷入口</h2>
          <div className="grid grid-cols-2 gap-4">
            <QuickEntry
              title="U币明细"
              icon="📋"
              onClick={() => navigate('/point-transactions')}
            />
            <QuickEntry
              title="我的纠错"
              icon="✏️"
              onClick={() => navigate('/my-corrections')}
            />
          </div>
        </div>

        {/* U币获取方式 */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">如何获得U币</h2>
          <div className="space-y-3">
            <EarnMethod
              icon="🔍"
              title="纠错奖励"
              description="发现题目或试卷错误并提交,审核通过后获得奖励"
              badge="+50币/处"
            />
          </div>
        </div>

      </div>
    </div>
  );
};

// 快捷入口组件
const QuickEntry: React.FC<{
  title: string;
  icon: string;
  onClick: () => void;
}> = ({ title, icon, onClick }) => {
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center gap-2 p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
    >
      <span className="text-3xl">{icon}</span>
      <span className="text-sm text-gray-700">{title}</span>
    </button>
  );
};

// U币获取方式组件
const EarnMethod: React.FC<{
  icon: string;
  title: string;
  description: string;
  badge: string;
}> = ({ icon, title, description, badge }) => {
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
      <span className="text-2xl flex-shrink-0">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-800">{title}</span>
          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
            {badge}
          </span>
        </div>
        <div className="text-sm text-gray-500 mt-1">{description}</div>
      </div>
    </div>
  );
};

export default PointCenter;
